"use client";

import { motion, useScroll, useTransform, type Variants } from "framer-motion";
import Image from "next/image";

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.1, ease: "easeOut" },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

export default function PrivacyClient() {
  const { scrollYProgress } = useScroll();
  const backgroundY = useTransform(scrollYProgress, [0, 1], ["0%", "15%"]);

  return (
    <div className="relative min-h-screen">
      {/* ── Fixed Parallax Aesthetic Background ── */}
      <div className="fixed inset-0 -z-10 bg-[var(--background)]">
        <motion.div
          className="absolute inset-0 h-[120vh] w-full"
          style={{ y: backgroundY }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-[var(--background)]/40 via-transparent to-[var(--background)]/90 mix-blend-multiply z-10" />
          <Image
            src="https://images.unsplash.com/photo-1770902634037-08b8e594168b?q=80&w=1974&auto=format&fit=crop"
            alt="Background"
            fill
            className="object-cover opacity-30 md:opacity-40 mix-blend-luminosity grayscale-[30%]"
          />
        </motion.div>
      </div>

      <div className="relative z-10 mx-auto max-w-4xl px-4 py-32 sm:px-8 lg:py-40">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="show"
          className="flex flex-col gap-8 rounded-[2rem] border border-white/10 bg-white/5 p-8 backdrop-blur-xl sm:p-12 shadow-2xl"
        >
          <motion.div variants={itemVariants}>
            <h1 className="text-3xl font-extrabold tracking-tight text-[var(--foreground)] sm:text-4xl">
              Privacy <span className="text-[var(--accent)]">Policy</span>
            </h1>
            <p className="mt-2 text-sm text-[var(--foreground-muted)]">Last updated: April 8, 2026</p>
          </motion.div>

          <motion.div variants={itemVariants} className="prose prose-invert max-w-none text-[var(--foreground)]/80 prose-headings:text-[var(--foreground)] prose-a:text-[var(--accent)]">
            <h2>1. Information We Collect</h2>
            <p>
              We collect information to provide better services to all our users. Information can be collected directly from you when you provide it to us, such as when you fill out a contact form, or automatically through your use of the website through cookies and related technologies.
            </p>

            <h2>2. How We Use the Information</h2>
            <p>
              The information we collect is used to understand user behavior, improve our website, respond to inquiries, and personalize the user experience. We may also use this information for internal analytics.
            </p>

            <h2>3. Information Sharing</h2>
            <p>
              We do not share personal information with companies, organizations, or individuals outside of our organization except in standard business operations (such as a database hosting provider) or when we have your consent, or for legal reasons.
            </p>

            <h2>4. Security</h2>
            <p>
              We work hard to protect our users from unauthorized access to or unauthorized alteration, disclosure, or destruction of information we hold. However, no internet or email transmission is ever fully secure or error free.
            </p>
            
            <h2>5. Your Rights</h2>
            <p>
              Depending on your regional location, you may have the right to access, update, or delete your personal data. To exercise these rights, please contact us directly using the contact methods provided on this website.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
